import { useEffect } from 'react';

/**
 * Tell crawlers that the current URL does not exist.
 *
 * Ariv is a client-rendered SPA on a static host, so the server answers 200
 * for every path.
 *
 * `robots: noindex` is the part that works everywhere: any crawler that runs
 * JavaScript reads it and drops the URL.
 *
 * `prerender-status-code` only ever did anything via Prerender.io, which read
 * it at the Cloudflare edge and turned it into a real HTTP 404. That service
 * was removed on 2026-10-15 (its token expired and every bot request started
 * returning an empty 503). The tag is harmless and left in place so the
 * behaviour returns if a prerendering proxy is ever reintroduced, but until
 * then an unknown URL answers 200 with a noindex page rather than a true 404.
 * Making the status real needs the static host to serve a 404 document, which
 * conflicts with the SPA catch-all and is its own piece of work.
 *
 * FIX (2026-10-05): the first version appended a second
 * `<meta name="robots">` next to the site default in public/index.html, so
 * a not-found page carried both "index, follow" and "noindex". Crawlers are
 * not required to agree on which wins. The existing tag is now overwritten
 * and restored on unmount, so there is only ever one.
 *
 * Pass `false` while the page is still loading, so a slow fetch is never
 * mistaken for a missing page.
 */
export default function useCrawlerNotFound(isMissing) {
  useEffect(() => {
    if (!isMissing) return undefined;

    const head = document.head;

    // Reuse the site-default robots tag rather than adding a second one.
    let robots = head.querySelector('meta[name="robots"]');
    const createdRobots = !robots;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      head.appendChild(robots);
    }
    const previousRobots = robots.getAttribute('content');
    robots.setAttribute('content', 'noindex');

    // Prerender.io removes this tag once it has read it, so there is nothing
    // to collide with; create it only if a previous render left one behind.
    let status = head.querySelector('meta[name="prerender-status-code"]');
    const createdStatus = !status;
    if (!status) {
      status = document.createElement('meta');
      status.setAttribute('name', 'prerender-status-code');
      head.appendChild(status);
    }
    status.setAttribute('content', '404');

    return () => {
      if (createdRobots) robots.remove();
      else if (previousRobots != null) robots.setAttribute('content', previousRobots);
      if (createdStatus) status.remove();
    };
  }, [isMissing]);
}
